/**
 * Default copy for the "Verification failed — Retry" state (ER-11459). Distinct from the
 * generation-failure banner ("Content generation failed") and the mismatch verdict copy.
 */
export const VERIFICATION_FAILED_DEFAULT_ERROR =
  "We couldn't verify this video against the checkpoint context. Retry to run the check again.";

/**
 * Copy for the "Verification failed — Retry" state when the IQ stream was cut short by the
 * client timeout or a dropped connection, rather than IQ reporting that the check could not
 * run (ER-12371 AC-7). Saying the video itself could not be verified is misleading here — the
 * check never produced a verdict, so retrying is the only meaningful action.
 */
export const VIDEO_CONTEXT_STREAM_INTERRUPTED_ERROR =
  "The video check timed out or lost its connection before it finished. Retry to run the check again.";

/**
 * Whether a context-validation payload indicates the check itself could not run
 * (VIDEO_CONTEXT_VERIFICATION_FAILED) — an infrastructure failure, distinct from a
 * mismatch verdict which is only cleared by replacing/deleting the video.
 *
 * @param {Object|null|undefined} suggestedInstructions
 * @returns {boolean}
 */
export function isContextVerificationFailed(suggestedInstructions) {
  if (!suggestedInstructions || typeof suggestedInstructions !== "object") return false;
  const status =
    suggestedInstructions.validation?.status ??
    suggestedInstructions.inspection_instructions?.validation?.status ??
    null;
  const statusUpper = typeof status === "string" ? status.toUpperCase() : "";
  if (
    statusUpper === "VIDEO_CONTEXT_VERIFICATION_FAILED" ||
    statusUpper === "VERIFICATION_FAILED"
  ) {
    return true;
  }
  const error = String(suggestedInstructions.error ?? "").toUpperCase();
  return (
    error.includes("VIDEO_CONTEXT_VERIFICATION_FAILED") || error.includes("VERIFICATION_FAILED")
  );
}

/**
 * Map IQ video_to_components complete payload → suggestedInstructions for
 * CheckpointEditFormV2 context validation (upload path).
 * Respects validation.status so PASS reasoning is never treated as a mismatch error.
 *
 * @param {Object|null|undefined} completeData - SSE complete event payload
 * @param {string|null} [fallbackVideoUrl] - Playable / iframe URL when payload has none
 * @returns {Object}
 */
export function buildContextValidationSuggestedInstructions(completeData, fallbackVideoUrl = null) {
  const payload = completeData && typeof completeData === "object" ? completeData : {};
  const validation = payload.validation ?? payload.inspection_instructions?.validation ?? null;
  const statusUpper = typeof validation?.status === "string" ? validation.status.toUpperCase() : "";
  const videoUrl = payload.video_url || fallbackVideoUrl || null;

  if (statusUpper === "PASS") {
    return {
      ...payload,
      validation: validation || { status: "PASS" },
      error: null,
      video_url: videoUrl,
    };
  }

  if (isContextVerificationFailed(payload)) {
    return {
      ...payload,
      validation: validation || { status: "VIDEO_CONTEXT_VERIFICATION_FAILED" },
      error: null,
      video_url: videoUrl,
    };
  }

  if (statusUpper === "FAIL" || statusUpper === "FAILED" || payload.error) {
    return {
      ...payload,
      validation: validation || { status: "FAIL" },
      error:
        payload.error ||
        validation?.reasoning ||
        "This video does not match the context of the checkpoint.",
      video_url: videoUrl,
    };
  }

  return {
    ...payload,
    validation,
    error: payload.error || "Could not validate video against checkpoint context.",
    video_url: videoUrl,
  };
}

/**
 * Whether context-validation suggestedInstructions should be treated as a fail/mismatch.
 * Explicit PASS always wins over a populated error/reasoning field.
 *
 * @param {Object|null|undefined} suggestedInstructions
 * @returns {boolean}
 */
export function isContextValidationFail(suggestedInstructions) {
  const validationStatus =
    suggestedInstructions?.validation?.status ??
    suggestedInstructions?.inspection_instructions?.validation?.status ??
    null;
  const statusUpper = typeof validationStatus === "string" ? validationStatus.toUpperCase() : "";
  if (statusUpper === "PASS") return false;
  // A verification failure is an infrastructure problem, never a mismatch verdict.
  if (isContextVerificationFailed(suggestedInstructions)) return false;
  return !!(suggestedInstructions?.error || statusUpper === "FAIL" || statusUpper === "FAILED");
}

/**
 * Persisted `proposed_instructions` from a prior FAIL persist — JSON string or object.
 * Used to clear leftover mismatch payloads after a later PASS.
 *
 * @param {unknown} instructions
 * @returns {boolean}
 */
export function isFailContextValidationInstructions(instructions) {
  if (instructions == null || instructions === "") return false;
  let parsed = instructions;
  if (typeof instructions === "string") {
    try {
      parsed = JSON.parse(instructions);
    } catch {
      return false;
    }
  }
  return isContextValidationFail(parsed);
}

/**
 * Checkpoint PATCH after context-validation PASS — clears a prior FAIL persist
 * so the mismatch screen does not stick.
 *
 * @param {unknown} proposedInstructions
 * @returns {{ proposed_video_microlearning_is_mismatch: false, proposed_instructions?: null }}
 */
export function buildClearContextValidationMismatchPatch(proposedInstructions) {
  const patch = { proposed_video_microlearning_is_mismatch: false };
  if (isFailContextValidationInstructions(proposedInstructions)) {
    patch.proposed_instructions = null;
  }
  return patch;
}

/**
 * Whether checkpoint persisted state indicates context-validation FAIL / mismatch.
 *
 * @param {Object|null|undefined} checkpoint
 * @returns {boolean}
 */
export function isVideoContextMismatchCheckpoint(checkpoint) {
  if (!checkpoint || typeof checkpoint !== "object") return false;
  if (checkpoint.proposed_video_microlearning_is_mismatch) return true;
  return isFailContextValidationInstructions(checkpoint.proposed_instructions);
}

/**
 * Error message from persisted FAIL `proposed_instructions`.
 *
 * @param {unknown} proposedInstructions
 * @returns {string|null}
 */
export function resolvePersistedVideoMismatchError(proposedInstructions) {
  if (!isFailContextValidationInstructions(proposedInstructions)) return null;
  let parsed = proposedInstructions;
  if (typeof proposedInstructions === "string") {
    try {
      parsed = JSON.parse(proposedInstructions);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  const message =
    parsed.error ??
    parsed.validation?.reasoning ??
    parsed.inspection_instructions?.validation?.reasoning;
  return message != null && String(message).trim() ? String(message).trim() : null;
}
